<template>
    <div class="organization-overview">
        <div class="rlms-fs-lg rlms-fw-600 rlms-mb-5">Overview</div>
        <template v-if="loading">
            <div class="box-lr-wrapper">
                <div class="box-left rlms-flex rlms-flex-col rlms-gap-20">
                    <!-- General Information Box -->
                    <div class="detail-box rlms-border rlms-br-16">
                        <SkeletonLoader class="rlms-flex" />
                    </div>
                </div>
                <div class="box-right rlms-flex rlms-flex-col rlms-gap-20">
                    <div class="detail-box rlms-border rlms-br-16">
                        <SkeletonLoader class="rlms-flex" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="box-lr-wrapper">
                <div class="box-left rlms-flex rlms-flex-col rlms-gap-20">
                    <!-- General Information Box -->
                    <div class="detail-box rlms-border rlms-br-16">
                        <div
                            class="detail-box-head rlms-flex rlms-justify-between rlms-items-center rlms-border-bottom rlms-flex-wrap rlms-gap-5">
                            <span class="rlms-fw-500 rlms-sm-sm">General
                                information</span>
                            <a href="javascript:void(0);"
                                class="rlms-flex rlms-items-center rlms-gap-5 rlms-cursor-pointer"
                                @click="openAddEditOrganization"
                                v-if="organization?.is_owner || organization?.is_administrator">
                                <img :src="editIcon" />
                                <p class="rlms-text-primary-600">Edit</p>
                            </a>
                        </div>
                        <div class="detail-box-body" id="general-info-content">
                            <ul class="db-ul-style-2">
                                <li>
                                    <span class="rlms-text-extra-3 rlms-fw-500">Organization Name:</span>
                                    <p>{{ organization?.name }}</p>
                                </li>
                                <li><span class="rlms-text-extra-3 rlms-fw-500">Created:</span>
                                    <p>{{ formatDate(organization?.created_at) }}</p>
                                </li>
                                <li><span class="rlms-text-extra-3 rlms-fw-500 rlms-self-baseline">Address:</span>
                                    <p class="rlms-text-end">
                                        <span>{{ organization?.address }}</span>,
                                        <span v-if="organization?.unit">{{ organization.unit }}, </span>
                                        <span v-if="organization?.city">{{ organization.city }},<br></span>
                                        <span v-if="organization?.state">{{ organization.state }},<br></span>
                                        <span v-if="organization?.country">{{ getCountryNameByISO2(organization.country)
                                        }}</span> -
                                        <span v-if="organization?.postal_code">{{ organization.postal_code }}</span>
                                    </p>

                                </li>
                                <li><span class="rlms-text-extra-3 rlms-fw-500">Regressions:</span>
                                    <p>{{ organization?.regressions ? 'Enabled' : 'Disabled' }}</p>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <template v-if="canConductAssessment">
                        <!-- Unassigned Subscriptions Box -->
                        <div class="detail-box rlms-border rlms-br-16" >
                            <div class="detail-box-head rlms-border-bottom"><span class="rlms-fw-500 rlms-sm-sm">Unassigned
                                    Subscriptions</span></div>
                            <!-- Show unassigned subscriptions if available -->
                            <!-- No unassigned subscriptions message -->
                             <template v-if="organization?.unassignedSubscriptionPayments?.length">
                                <div class="rlms-table">
                                    <div class="rlms-table-inner">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Purchaser</th>
                                                    <th>Product</th>
                                                    <th>QTY Left</th>
                                                    <th>Amount</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="purchase in organization?.unassignedSubscriptionPayments" :key="purchase.id">
                                                    <td>{{ purchase.user?.firstname }} {{ purchase.user?.lastname }}</td>
                                                    <td>{{ purchase.product }}</td>
                                                    <td>{{ purchase.remaining }}</td>
                                                    <td>{{ formatCurrency(purchase.amount * purchase.quantity) }}</td>
                                                    <td>{{ formatDate(purchase.created_at) }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="detail-box-body rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center">
                                    <img :src="noData1" class="rlms-mx-auto"
                                        style="width: clamp(180px, -1533rem + 3200vw, 300px);">
                                    <p>No assessment subscriptions have been purchased or
                                        all purchased subscriptions have been assigned to learners.</p>
                                </div>
                            </template>
                        </div>
                        
                        <!-- Recent Purchases Box -->
                        <div class="detail-box rlms-border rlms-br-16">
                            <div
                                class="detail-box-head rlms-flex rlms-justify-between rlms-items-center rlms-border-bottom rlms-flex-wrap rlms-gap-5">
                                <span class="rlms-fw-500 rlms-sm-sm">Recent Purchases</span>
                                <RouterLink :to="`/organizations/${organizationId}/subscription/purchase-history`">
                                    <p class="rlms-text-primary-600">View All</p>
                                </RouterLink>
                            </div>
                            <div class="rlms-table">
                                <div class="rlms-table-inner">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Purchaser</th>
                                                <th>Product</th>
                                                <th>QTY</th>
                                                <th>Amount</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-if="organization?.recentPurchases?.length">
                                                <tr v-for="purchase in organization?.recentPurchases" :key="purchase.id">
                                                    <td>{{ purchase.user?.firstname }} {{ purchase.user?.lastname }}</td>
                                                    <td>{{ purchase.product }}</td>
                                                    <td>{{ purchase.quantity }}</td>
                                                    <td>{{ formatCurrency(purchase.amount * purchase.quantity) }}</td>
                                                    <td>{{ formatDate(purchase.created_at) }}</td>
                                                </tr>
                                            </template>
                                            <tr v-else>
                                                <td colspan="5">
                                                    <div
                                                        class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center rlms-p-3">
                                                        <img :src="noDataImg" class="rlms-mx-auto"
                                                            style="width: clamp(150px, -1533rem + 3200vw, 150px);">
                                                        <p class="rlms-text-center">No purchase history
                                                            found.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </template>

                </div>

                <!-- Right column -->
                <div class="box-right rlms-flex rlms-flex-col rlms-gap-20">
                    <!-- Learners Box -->
                    <div class="detail-box rlms-border rlms-br-16">
                        <div class="detail-box-head rlms-border-bottom"><span
                                class="rlms-fw-500 rlms-font-sm">Learners</span>
                        </div>
                        <div class="detail-box-body">
                            <div class="rlms-flex rlms-flex-col rlms-gap-20">
                                <div
                                    class="rlms-flex rlms-flex-col rlms-gap-8 rlms-bg-success-100 rlms-py-3 rlms-px-5 rlms-br-16">
                                    <p class="rlms-text-success-600 rlms-fw-500">Active Learners</p>
                                    <span class="rlms-text-success-600 rlms-fw-500">{{
                                        organization?.active_learners_count
                                        ?? 0 }}</span>
                                </div>
                                <div
                                    class="rlms-flex rlms-flex-col rlms-gap-8 rlms-bg-danger-100 rlms-py-3 rlms-px-5 rlms-br-16">
                                    <p class="rlms-text-danger-600 rlms-fw-500">Inactive Learners</p>
                                    <span class="rlms-text-danger-600 rlms-fw-500">{{
                                        organization?.inactive_learners_count
                                        ?? 0 }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Administrators Box -->
                    <div class="detail-box rlms-border rlms-br-16">
                        <div class="detail-box-head rlms-border-bottom"><span
                                class="rlms-fw-500 rlms-sm-sm">Administrators</span></div>
                        <div class="rlms-table">
                            <div class="rlms-table-inner">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <!-- Owner -->
                                        <tr>
                                            <td>
                                                <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc">
                                                    <img :src="adminInfo" style="width: 40px;">
                                                    <div class="rlms-flex rlms-flex-col">
                                                        <div class="rlms-fw-500">
                                                            {{
                                                                `${organization?.user?.firstname}
                                                            ${organization?.user?.lastname}` }}
                                                            <small>(Owner)</small>
                                                        </div><span class="rlms-fs-xxs rlms-text-extra-3">{{
                                                            `${organization?.user?.email}` }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <template v-if="organization?.administrators?.length">
                                            <tr v-for="admin in organization?.administrators" :key="admin.id">
                                                <td>
                                                    <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc">
                                                        <img :src="adminInfo" style="width: 40px;">
                                                        <div class="rlms-flex rlms-flex-col">
                                                            <div class="rlms-fw-500">
                                                                {{ `${admin?.firstname} ${admin?.lastname}` }}
                                                            </div>
                                                            <span class="rlms-fs-xxs rlms-text-extra-3">{{
                                                                `${admin?.email}`
                                                                }}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
    <AddEditOrganizationModal v-if="showOrganizationModal" @close="showOrganizationModal = false;" @saved="init()"
        :organization="selectedOrganization" />
</template>
<script setup>
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import axios from "@/services/axios";
import { formatCurrency, formatDate } from '@/utils/format-helpers'
import AddEditOrganizationModal from '@/components/AddEditOrganizationModal.vue';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import { getCountryNameByISO2 } from '@/utils/countries';
import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue';
import editIcon from '@/assets/images/edit-icon.png'
import noData1 from '@/assets/images/no-data1.png'
import noDataImg from '@/assets/images/no-data-img.png'
import adminInfo from '@/assets/images/admin-info.png'
import { authrizedPermissions } from '@/utils/permissions';

const route = useRoute()
const loader = useLoaderStore();

const organizationId = route.params.organization;
const organization = ref(null);
const selectedOrganization = ref(null);
const showOrganizationModal = ref(false);

const loading = computed(() => {
    return loader.loading;
});

onMounted(async () => {
    init();
});

const init = () => {
    showOrganizationModal.value = false;
    fetchOrganization();
}

const fetchOrganization = async () => {
    try {
        loader.show();

        const { data } = await axios.get(`/organizations/${organizationId}`);

        organization.value = data.data;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const openAddEditOrganization = () => {
    selectedOrganization.value = organization.value;
    showOrganizationModal.value = true;
}

const canConductAssessment = computed(() => {
    return (organization.value?.is_owner || organization.value?.is_administrator ||
            (organization.value?.is_authorized_user && organization.value?.permissions?.[authrizedPermissions.conduct_view_assessments])
        )
});
</script>